import { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import html2canvas from "html2canvas";
import JSZip from "jszip";

const ANCHOR_STORAGE_KEY = "qa-about-anchor-v1";

type Breakpoint = "mobile" | "tablet" | "desktop";

const DEFAULTS: Record<Breakpoint, { x: number; y: number }> = {
  mobile: { x: 50, y: 28 },
  tablet: { x: 50, y: 32 },
  desktop: { x: 52, y: 38 },
};

type Viewport = {
  label: string;
  device: Breakpoint;
  width: number;
  height: number;
};

const VIEWPORTS: Viewport[] = [
  { label: "iPhone SE", device: "mobile", width: 375, height: 667 },
  { label: "iPhone 13/14", device: "mobile", width: 390, height: 844 },
  { label: "iPhone 14 Pro Max", device: "mobile", width: 430, height: 932 },
  { label: "Pixel 7", device: "mobile", width: 412, height: 915 },
  { label: "iPad Mini (portrait)", device: "tablet", width: 768, height: 1024 },
  { label: "iPad Pro 11 (portrait)", device: "tablet", width: 834, height: 1194 },
  { label: "iPad Pro 11 (landscape)", device: "tablet", width: 1194, height: 834 },
  { label: "Laptop", device: "desktop", width: 1280, height: 800 },
  { label: "MacBook Pro 14", device: "desktop", width: 1512, height: 982 },
  { label: "Desktop HD", device: "desktop", width: 1440, height: 900 },
  { label: "Desktop FHD", device: "desktop", width: 1920, height: 1080 },
];

const GROUPS: Breakpoint[] = ["mobile", "tablet", "desktop"];

/**
 * Visual QA harness for the ABOUT portrait. Mirrors /qa/hero but writes
 * to `--about-anchor-*-{mobile|tablet|desktop}` CSS variables in
 * `src/index.css`. Tune each device group independently and copy the
 * generated snippet back into the project — no component edits needed.
 */
export default function QAAbout() {
  const [scale, setScale] = useState(0.5);
  const [showRulers, setShowRulers] = useState(true);
  const [anchors, setAnchors] = useState(DEFAULTS);
  const [exporting, setExporting] = useState<null | { done: number; total: number }>(null);
  const captureHostRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(ANCHOR_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        setAnchors((prev) => ({
          mobile: { ...prev.mobile, ...(parsed.mobile ?? {}) },
          tablet: { ...prev.tablet, ...(parsed.tablet ?? {}) },
          desktop: { ...prev.desktop, ...(parsed.desktop ?? {}) },
        }));
      }
    } catch {
      /* ignore */
    }
  }, []);
  useEffect(() => {
    try {
      localStorage.setItem(ANCHOR_STORAGE_KEY, JSON.stringify(anchors));
    } catch {
      /* ignore */
    }
  }, [anchors]);

  const cssSnippet = useMemo(
    () =>
      `:root {\n` +
      `  --about-anchor-x-mobile: ${anchors.mobile.x}%;\n` +
      `  --about-anchor-y-mobile: ${anchors.mobile.y}%;\n` +
      `  --about-anchor-x-tablet: ${anchors.tablet.x}%;\n` +
      `  --about-anchor-y-tablet: ${anchors.tablet.y}%;\n` +
      `  --about-anchor-x-desktop: ${anchors.desktop.x}%;\n` +
      `  --about-anchor-y-desktop: ${anchors.desktop.y}%;\n` +
      `}`,
    [anchors],
  );

  const anchorQueryFor = (bp: Breakpoint) => {
    const { x, y } = anchors[bp];
    return `?x=${encodeURIComponent(`${x}%`)}&y=${encodeURIComponent(`${y}%`)}`;
  };

  const handleExport = async () => {
    const host = captureHostRef.current;
    if (!host) return;
    const zip = new JSZip();
    const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
    const folder = zip.folder(`about-qa-${stamp}`)!;
    folder.file(
      "anchors.css",
      cssSnippet + "\n\n/* Exported " + new Date().toISOString() + " */\n",
    );

    setExporting({ done: 0, total: VIEWPORTS.length });
    try {
      for (let i = 0; i < VIEWPORTS.length; i++) {
        const vp = VIEWPORTS[i];
        const q = anchorQueryFor(vp.device);
        const iframe = document.createElement("iframe");
        iframe.style.cssText = `width:${vp.width}px;height:${vp.height}px;border:0;display:block;`;
        iframe.src = `/qa/about/frame${q}`;
        host.appendChild(iframe);
        await new Promise<void>((resolve) => {
          iframe.addEventListener("load", () => resolve(), { once: true });
        });
        await new Promise((r) => setTimeout(r, 450));
        try {
          const doc = iframe.contentDocument;
          if (doc && doc.documentElement) {
            const canvas = await html2canvas(doc.documentElement, {
              width: vp.width,
              height: vp.height,
              windowWidth: vp.width,
              windowHeight: vp.height,
              backgroundColor: null,
              useCORS: true,
              scale: 1,
            });
            const blob: Blob | null = await new Promise((res) =>
              canvas.toBlob((b) => res(b), "image/png"),
            );
            if (blob) {
              const safe = vp.label.replace(/[^a-z0-9]+/gi, "-").toLowerCase();
              folder.file(
                `${String(i + 1).padStart(2, "0")}-${vp.device}-${vp.width}x${vp.height}-${safe}.png`,
                blob,
              );
            }
          }
        } finally {
          host.removeChild(iframe);
        }
        setExporting({ done: i + 1, total: VIEWPORTS.length });
      }

      const zipBlob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `about-qa-${stamp}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(null);
    }
  };

  return (
    <>
      <Helmet>
        <title>About Portrait — Visual QA</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <main className="min-h-screen bg-navy-deep text-foreground">
        <header className="sticky top-0 z-20 border-b border-border bg-navy-deep/95 backdrop-blur px-6 py-4">
          <div className="mx-auto max-w-7xl flex flex-wrap items-center gap-4 justify-between">
            <div>
              <h1 className="font-display text-xl tracking-wide">
                About Portrait · Visual QA
              </h1>
              <p className="text-xs text-silver/70">
                Per-breakpoint anchors for the ABOUT image. Tune mobile /
                tablet / desktop independently — the CSS snippet below maps
                1:1 to
                <code className="mx-1 text-gold">--about-anchor-*</code>
                in <code className="text-gold">src/index.css</code>.
              </p>
            </div>
            <div className="flex items-center gap-4 text-xs text-silver/80">
              <label className="flex items-center gap-2">
                Preview scale
                <input
                  type="range"
                  min={0.25}
                  max={1}
                  step={0.05}
                  value={scale}
                  onChange={(e) => setScale(parseFloat(e.target.value))}
                />
                <span className="tabular-nums w-10">{Math.round(scale * 100)}%</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={showRulers}
                  onChange={(e) => setShowRulers(e.target.checked)}
                />
                Show rulers
              </label>
              <button
                type="button"
                onClick={() => setAnchors(DEFAULTS)}
                className="px-3 py-1.5 rounded border border-border hover:bg-navy-elevated"
              >
                Reset all
              </button>
              <button
                type="button"
                onClick={() => navigator.clipboard?.writeText(cssSnippet)}
                className="px-3 py-1.5 rounded border border-gold/50 text-gold hover:bg-gold/10"
                title="Copy CSS to paste into src/index.css"
              >
                Copy CSS
              </button>
              <button
                type="button"
                onClick={handleExport}
                disabled={!!exporting}
                className="px-3 py-1.5 rounded border border-emerald/60 text-emerald hover:bg-emerald/10 disabled:opacity-60 disabled:cursor-wait"
                title="Capture every viewport at native size and download a ZIP of PNGs"
              >
                {exporting
                  ? `Exporting ${exporting.done}/${exporting.total}…`
                  : "Export ZIP"}
              </button>
            </div>
          </div>

          <div className="mx-auto max-w-7xl mt-4 grid gap-4 md:grid-cols-3 text-xs text-silver/80">
            {GROUPS.map((bp) => (
              <div
                key={bp}
                className="rounded border border-border/60 p-3 space-y-2 bg-navy-elevated/40"
              >
                <div className="font-display uppercase tracking-[0.25em] text-[10px] text-gold">
                  {bp}
                </div>
                {(["x", "y"] as const).map((axis) => (
                  <label key={axis} className="flex items-center gap-2">
                    <span className="w-8 shrink-0 uppercase">{axis}</span>
                    <input
                      type="range"
                      min={0}
                      max={100}
                      step={0.5}
                      value={anchors[bp][axis]}
                      onChange={(e) =>
                        setAnchors((prev) => ({
                          ...prev,
                          [bp]: {
                            ...prev[bp],
                            [axis]: parseFloat(e.target.value),
                          },
                        }))
                      }
                      className="flex-1"
                    />
                    <span className="tabular-nums w-12 text-right">
                      {anchors[bp][axis].toFixed(1)}%
                    </span>
                  </label>
                ))}
              </div>
            ))}
          </div>
          <pre className="mx-auto max-w-7xl mt-2 text-[10px] text-silver/50 whitespace-pre-wrap">
{cssSnippet}
          </pre>
        </header>

        <div className="mx-auto max-w-7xl px-6 py-8 space-y-12">
          {GROUPS.map((group) => {
            const items = VIEWPORTS.filter((v) => v.device === group);
            const q = anchorQueryFor(group);
            return (
              <section key={group} className="space-y-4">
                <h2 className="font-display uppercase tracking-[0.3em] text-xs text-gold">
                  {group} · anchor {anchors[group].x.toFixed(1)}% /{" "}
                  {anchors[group].y.toFixed(1)}%
                </h2>
                <div className="flex flex-wrap gap-8">
                  {items.map((vp) => (
                    <QAFrame
                      key={vp.label}
                      viewport={vp}
                      scale={scale}
                      showRulers={showRulers}
                      anchorQuery={q}
                    />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
        {/* Offscreen host for native-size capture during export. */}
        <div
          ref={captureHostRef}
          aria-hidden
          style={{
            position: "fixed",
            left: "-100000px",
            top: 0,
            width: 0,
            height: 0,
            overflow: "hidden",
            pointerEvents: "none",
          }}
        />
      </main>
    </>
  );
}

function QAFrame({
  viewport,
  scale,
  showRulers,
  anchorQuery,
}: {
  viewport: Viewport;
  scale: number;
  showRulers: boolean;
  anchorQuery: string;
}) {
  const { label, width, height } = viewport;
  return (
    <figure
      className="flex flex-col gap-2"
      style={{ width: width * scale }}
    >
      <figcaption className="flex items-baseline justify-between text-xs text-silver/80">
        <span className="font-medium">{label}</span>
        <span className="tabular-nums text-silver/60">
          {width}×{height}
        </span>
      </figcaption>
      <div
        className="relative border border-border/60 shadow-portrait rounded-sm overflow-hidden bg-navy"
        style={{ width: width * scale, height: height * scale }}
      >
        <iframe
          title={`About preview — ${label}`}
          src={`/qa/about/frame${anchorQuery}`}
          style={{
            width,
            height,
            border: 0,
            transform: `scale(${scale})`,
            transformOrigin: "top left",
          }}
          loading="lazy"
        />
        {showRulers && (
          <div aria-hidden className="pointer-events-none absolute inset-0">
            <div
              className="absolute left-0 right-0 border-t border-emerald/70"
              style={{ top: `${(1 / 3) * 100}%` }}
            />
            <div
              className="absolute left-0 right-0 border-t border-emerald/30"
              style={{ top: `${(2 / 3) * 100}%` }}
            />
          </div>
        )}
      </div>
    </figure>
  );
}
